// lib
import React from 'react';
import { useHistory } from 'react-router';

// redux
import { useSelector } from 'react-redux';

// styles
import {
  DeltaCommunicationContainer,
  DeltaCommunicationText,
  RightArrowWrapper,
  DeltaCommunicationMetText,
  DeltaCommunicationMet,
  OfferAppliedWrapper,
} from '../style';

// selector
import { getProductId } from 'shared/store/details/selectors';

// icons
import { useStoreIcon, IconName } from 'shared/icons';

// hooks
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// types
import { Theme } from '@nykaa/ui-components/themes/types';
import { DeltaConfigType } from 'shared/layouts/Coupons/components/DeltaCommunication/constants';
import {
  DELTA_COMM_STATUS,
  Coupon,
  CouponDetails,
  COUPON_STATUS,
} from 'shared/layouts/Coupons/types';
import { COUPON_CLICK_LOCATION_TYPE } from 'shared/layouts/Coupons/constants';

// constants
import {
  TEXT,
  CHEVRON_RIGHT_SIZE,
  TICK_MARK_SIZE,
  PLP_REDIRECT_URL,
  COUPON_CLICK_TYPE,
} from './constants';

// analytics
import { onCouponClick } from './analytics';
import {
  onCouponShopMoreClicked,
  onOfferShopMoreClicked,
  onCouponShopMoreClickedCart,
} from 'shared/layouts/Coupons/analytics';

// helpers
import { DEVICE_TYPE, getDeviceType } from 'shared/helpers/app';
import { goToProduct as goToOfferListing } from 'shared/helpers/messaging';

const DeltaCommunication = ({
  deltaModifiedText,
  isDetailsView,
  deltaAmount = 0,
  isOffer = false,
  status,
  couponDetails,
  offerId,
  isPDP = false,
  couponData,
  viewProductsFlag = false,
}: {
  deltaModifiedText: string;
  isDetailsView: boolean;
  deltaAmount?: number;
  isOffer?: boolean;
  status: DELTA_COMM_STATUS;
  couponDetails: CouponDetails;
  offerId?: number;
  isPDP?: boolean;
  couponData: Coupon;
  viewProductsFlag?: boolean;
}) => {
  const theme: Theme = useTheme();
  const deltaCommunicationConfig: DeltaConfigType = useRemoteConfig('deltaCommunication');
  const ChevronRightIcon = useStoreIcon(IconName.CHEVRON_RIGHT);
  const TickMarkIcon = useStoreIcon(IconName.ALERT_POSITIVE_FILLED);

  const couponStatus = couponDetails?.couponStatus;
  const couponCode = couponDetails?.couponCode;
  const productId = useSelector(getProductId);
  const isMobile = getDeviceType() === DEVICE_TYPE.MOBILE;
  const history = useHistory();

  if (!isMobile && !deltaCommunicationConfig?.enableDesktop) return null;

  const {
    offerSuccessMessage = '',
    couponSuccessMessage = '',
    deltaOfferColor = '',
    deltaCouponColor = '',
    deltaOfferTextColor = '',
    deltaCouponTextColor = '',
    deltaOfferSuccessColor = '',
    deltaCouponSuccessColor = '',
    deltaOfferSuccessTextColor = '',
    deltaCouponSuccessTextColor = '',
  } = deltaCommunicationConfig;

  const priceText = TEXT.SHOP_MORE.replace('<price>', deltaAmount?.toString());

  const onGotoProductsClicked = (couponType: string, offerIdRedirect: number) => {
    let couponClickType;

    if (isPDP) {
      if (isOffer) {
        onOfferShopMoreClicked(couponData, true, isDetailsView);
        if (isDetailsView) {
          couponClickType = COUPON_CLICK_LOCATION_TYPE.PDP_OFFERS_WIDGET_SEE_DETAILS;
        } else {
          couponClickType = COUPON_CLICK_LOCATION_TYPE.PDP_OFFERS_WIDGET_DELTA;
        }
      } else {
        onCouponShopMoreClicked(couponData, true);
        if (isDetailsView) {
          couponClickType = COUPON_CLICK_LOCATION_TYPE.PDP_COUPONS_WIDGET_SEE_DETAILS;
        } else {
          couponClickType = COUPON_CLICK_LOCATION_TYPE.PDP_COUPONS_WIDGET_DELTA;
        }
      }
    } else {
      onCouponShopMoreClickedCart(couponData, true, false);
      if (isDetailsView) {
        couponClickType = COUPON_CLICK_LOCATION_TYPE.COUPONS_PAGE_WIDGET_SEE_DETAILS;
      } else {
        couponClickType = COUPON_CLICK_LOCATION_TYPE.COUPONS_PAGE_WIDGET_DELTA;
      }
    }

    onCouponClick({ pageType: couponType, couponClickType: couponClickType });
    let url = '';
    if (isOffer) {
      url = `${PLP_REDIRECT_URL}/${offerIdRedirect}?product_id=${productId}`;
      if (!isMobile && isPDP) {
        window.open(url, '_blank');
        return;
      }
    } else {
      const urlCouponCode = couponCode ? couponCode : couponData?.couponDetails?.couponCode;
      url = `${PLP_REDIRECT_URL}/${offerIdRedirect}?coupon_offer_id=${offerIdRedirect}&couponId=${urlCouponCode}`;
      if (!isMobile && !isPDP) {
        goToOfferListing(url);
        return;
      }
      if (!isMobile && isPDP) {
        window.open(url, '_blank');
        return;
      }
    }
    history.push(url);
  };

  if (
    isOffer &&
    (status === DELTA_COMM_STATUS.APPLIED || status === DELTA_COMM_STATUS.APPLICABLE)
  ) {
    return (
      <OfferAppliedWrapper
        backgroundColor={deltaOfferSuccessColor}
        textColor={deltaOfferSuccessTextColor}
        isDetailsView={isDetailsView}
      >
        <TickMarkIcon
          size={TICK_MARK_SIZE}
          color={deltaOfferSuccessTextColor || theme.colors.positive}
        />
        <span>{offerSuccessMessage || TEXT.OFFER_APPLIED}</span>
      </OfferAppliedWrapper>
    );
  }

  if (isOffer && deltaModifiedText) {
    return (
      <DeltaCommunicationContainer backgroundColor={deltaOfferColor} isDetailsView={isDetailsView}>
        <DeltaCommunicationText textColor={deltaOfferTextColor}>
          {deltaModifiedText || priceText}
        </DeltaCommunicationText>
        <RightArrowWrapper onClick={() => onGotoProductsClicked(COUPON_CLICK_TYPE.OFFER, offerId)}>
          <ChevronRightIcon color={theme.colors.secondary} size={CHEVRON_RIGHT_SIZE} />
        </RightArrowWrapper>
      </DeltaCommunicationContainer>
    );
  }

  if (
    !isOffer &&
    (couponStatus === COUPON_STATUS.APPLIED || couponStatus === COUPON_STATUS.APPLICABLE)
  ) {
    return (
      <DeltaCommunicationMet successColor={deltaCouponSuccessColor} isDetailsView={isDetailsView}>
        <DeltaCommunicationMetText successTextColor={deltaCouponSuccessTextColor}>
          {couponSuccessMessage || TEXT.APPLY_COUPON_BAG}
        </DeltaCommunicationMetText>
      </DeltaCommunicationMet>
    );
  }

  // Coupon without success but valid delta
  if (!isOffer && deltaModifiedText) {
    return (
      <DeltaCommunicationContainer backgroundColor={deltaCouponColor} isDetailsView={isDetailsView}>
        <DeltaCommunicationText textColor={deltaCouponTextColor}>
          {deltaModifiedText || priceText}
        </DeltaCommunicationText>
        {viewProductsFlag && (
          <RightArrowWrapper
            onClick={() => onGotoProductsClicked(COUPON_CLICK_TYPE.COUPON, offerId, couponCode)}
          >
            <ChevronRightIcon color={theme.colors.secondary} size={CHEVRON_RIGHT_SIZE} />
          </RightArrowWrapper>
        )}
      </DeltaCommunicationContainer>
    );
  }

  return null;
};

export default DeltaCommunication;
